import _ from 'lodash';
import moment from 'moment';

import {Spreadsheet} from '@/components/spreadsheet/spreadsheet';
import {
  EconomicGroup,
  IEconomicGroupGoalsDetails
} from '@/modules/business-plan/modules/approval/pages/ford-goal-settings/types';

export class BpaSpreadsheetGoalsPerMonth extends Spreadsheet {
  name: string = 'goals_per_month';
  public moment = moment.locale('pt-BR');

  mount(dataSource: EconomicGroup[]) {
    const dataSourceGroup = _(dataSource[0].details).groupBy(x => x.family.name).value();

    const sectionHeader: Array<any> = [
      { key: 'main', text: '', index: 0 },
      { key: 'main', text: 'TOYOTA - ESTRATÉGIA LOCAL', index: 1 },
    ];

    const sectionAreaInfo: Array<any> = [
      { key: 'main', text: '', index: 0 },
      { key: 'main', text: `${dataSource[0].nameLocaleType.toUpperCase()}: ${dataSource[0].nameLocale.toUpperCase()}`, index: 1 }
    ];

    const sectionGroupInfo: Array<any> = [
      { key: 'main', text: `DATA: ${moment().format('DD/MM/YYYY HH:mm')}`, index: 0 },
      { key: 'main', text: `GRUPO: ${dataSource[0].nameGroup} DISTRIBUIÇÃO METAS (2021)`, index: 1 }
    ];

    const sectionColumns: any = [
      {
        key: 'main',
        source: [
          {key: 'nameFamily', text: 'FAMÍLIA'},
          {key: 'saleChannel', text: 'CANAL'},
          {key: 'total', text: 'TOTAL'}
        ]
      },
      {
        key: 'main',
        source: _.map([...Array(12).keys()], d => ({
            key: 'value',
            text: moment()
              .startOf('year')
              .add(d, 'months')
              .format('MMM')
              .toUpperCase(),
            format: '#,0'
          }))
      }
    ];

    const baseStyle: any = {
      color: '#212121',
      textAlign: 'left',
      fontSize: 9,
      verticalAlign: 'center',
      fontFamily: 'Calibri'
    };

    const baseHeaderStyle: any = {
      color: '#212121',
      fontSize: 9,
      bold: true,
      textAlign: 'center',
      verticalAlign: 'center',
      fontFamily: 'Calibri'
    };

    const baseColorStyle: any = {
      main: '#bdbdbd',
      nameFamily: '#bdbdbd',
      saleChannel: '#bdbdbd',
      total: '#e57373',
      value: '#ffb74d',
      '3': '#e57373'
    };

    const baseBorderColorStyle: any = {
      main: '#e0e0e0',
      nameFamily: '#e0e0e0',
      saleChannel: '#e0e0e0',

      total: '#ffcdd2',
      value: '#ffe0b2',
      '3': '#ffcdd2'
    };

    const baseBackgroundColorStyle: any = {
      main: '#fafafa',
      nameFamily: '#fafafa',
      saleChannel: '#fafafa',

      total: '#fff5f6',
      value: '#fffbf5',
      '3': '#fff5f6'
    };

    const headerStyle = _.reduce(
      baseColorStyle,
      (acc: any, val: string, key: string) => {
        acc[key] = _.assign({}, baseHeaderStyle, { background: baseColorStyle[key] });
        return acc;
      },
      {}
    );

    const getCellStyle = (key: any) => {
      return _.assign({}, baseStyle, {
        textAlign: 'center',
        background: baseBackgroundColorStyle[key],
        borderLeft: { color: baseBorderColorStyle[key], size: 1 },
        borderRight: { color: baseBorderColorStyle[key], size: 1 },
        borderTop: { color: baseBorderColorStyle[key], size: 1 },
        borderBottom: { color: baseBorderColorStyle[key], size: 1 }
      });
    };

    this.workbookOptions = {
      sheets: _.map(dataSourceGroup, (data: IEconomicGroupGoalsDetails[], nameFamily: string) =>
          ({
            name: nameFamily,
            frozenColumns: 3,
            frozenRows: 2,
            rows: [
              {
                cells: [
                    ..._.map(sectionHeader, (section: any) =>
                    _.assign({}, headerStyle[section.key], {
                      value: section.text,
                      index: section.index,
                      borderLeft: { color: baseBorderColorStyle[section.key], size: 1 },
                      borderRight: { color: baseBorderColorStyle[section.key], size: 1 },
                      borderTop: { color: baseBorderColorStyle[section.key], size: 1 },
                      borderBottom: { color: baseBorderColorStyle[section.key], size: 1 }
                    }))
                ]
              },
              {
                cells: [
                  ..._.map(sectionAreaInfo, (section: any) =>
                    _.assign({}, headerStyle[section.key], {
                      value: section.text,
                      index: section.index,
                      borderLeft: { color: baseBorderColorStyle[section.key], size: 1 },
                      borderRight: { color: baseBorderColorStyle[section.key], size: 1 },
                      borderTop: { color: baseBorderColorStyle[section.key], size: 1 },
                      borderBottom: { color: baseBorderColorStyle[section.key], size: 1 }
                    }))
                ]
              },
              {
                cells: [
                  ..._.map(sectionGroupInfo, (section: any) =>
                      _.assign({}, headerStyle[section.key], {
                        value: section.text,
                        index: section.index,
                        borderLeft: { color: baseBorderColorStyle[section.key], size: 1 },
                        borderRight: { color: baseBorderColorStyle[section.key], size: 1 }
                      }))
                ]
              },
              {
                cells: [
                  ..._(sectionColumns)
                    .map((columns: any, key: any) =>
                      _.map(columns.source, (cell: any) => {
                        return _.assign({}, headerStyle[cell.key], {
                          value: cell.text,
                          wrap: true,
                          borderLeft: { color: baseBorderColorStyle[columns.key], size: 1 },
                          borderRight: { color: baseBorderColorStyle[columns.key], size: 1 },
                          borderTop: { color: baseBorderColorStyle[columns.key], size: 1 },
                          borderBottom: { color: baseBorderColorStyle[columns.key], size: 1 }
                        });
                        }
                      )
                    )
                    .flatten()
                    .value()
                ]
              },
              ...data.map(detail => {
                return detail.family.saleChannels
                  .map(channel => {
                    return {
                      nameFamily: detail.family.name,
                      saleChannel: channel.text,
                      total: detail.goalPerMonths.filter(x => x.saleChannel === channel.value).sumBy(x => x.goalValue),
                      values: detail.goalPerMonths
                        .filter(x => x.saleChannel === channel.value)
                        .sortBy(x => x.month)
                        .map(x => ({
                          month: moment()
                            .startOf('year')
                            .add(x.month - 1, 'months')
                            .format('MMM')
                            .toUpperCase(),
                          value: x.goalValue
                        }))
                    }
                  });
                })
                .flat()
                .map(d => {
                  return  {
                    cells: [
                      ..._(sectionColumns)
                        .map((columns: any, key: any) =>
                          _.map(columns.source, (cell: any, index: number) => {
                            if(cell.key === 'value') {
                              return _.assign({}, getCellStyle(cell.key), {
                                value: d.values.findOrFirst(x => x.month === cell.text).value,
                                format: cell.format
                              })
                            }

                            return _.assign({}, getCellStyle(cell.key), {
                              value: d[cell.key],
                              format: cell.format,
                              textAlign: cell.textAlign || 'center'
                            });
                          })
                        )
                        .flatten()
                        .value()
                    ]
                  }
              })
            ],
            columns: [
              {
                index: 0,
                width: 150
              },
              ..._.map(_.range(1, 50), index => ({
                index: index,
                width: 75
              }))
            ],
            showGridLines: false,
            mergedCells: [
              'B1:O1',
              'B2:O2',
              'B3:O3',
            ]
          } as any)
      )
    };

    return this;
  }
}
